import * as React from 'react'

import { ResponsiveLine } from '@nivo/line'

import i18n from 'modules/core/lib/i18n'

import { formatterForGraph, validateFieldType } from 'modules/reports/lib/Constants'
import type ReportStruct from 'modules/reports/lib/ReportStruct'

import styles from './index.module.css'

interface LegendProps {
  id: string
  fill: string
  size?: number
  width?: number
  height?: number
}

interface Props {
  munched: Array<{
    id: string
    data: Array<{ x: string | number; y: number }>
  }>
  data: Array<Record<string, unknown>>
  reportStruct: ReportStruct
  showLegend: boolean
  chartData: Record<string, unknown>
}

const Legend = ({ id, fill, width = 20, height = 20 }: LegendProps) => (
  <span className={styles.legend} key={id}>
    <svg
      style={{
        width,
        height,
      }}
    >
      <rect width={width} height={height} fill={fill} />
    </svg>
    <span className={styles.label}>{id}</span>
  </span>
)

const Legends: React.FC<{ legends: LegendProps[] }> = ({ legends = [] }) => (
  <div className={styles.legends}>
    {legends.map((legend) => (
      <Legend key={legend.id} {...legend} />
    ))}
  </div>
)

/**
 * LinesChart uses the data structure from the Area muncher to render the chart.
 *
 * The Lines muncher calculates the minimum values in order to render the X axis
 * from this minimum value.
 *
 * The lines muncher internally uses the PointDimensions class. the PoinDimension class
 * generates an array of object with X and Y axis, in order to use it with charts that
 * requires a X and Y axis such as Area and Lines charts.
 *
 * The lines muncher generates with the memorizedObject method a default set of data
 * that set each value to zero, in order to display each line that starts from zero.
 *
 * Then Lines muncher generates the processedData. the processedData contains
 * an array of object with the X and Y keys, these keys represents a point in the Lines
 * to pain in the chart canvas, and finally the id key. The id keys is the projection
 * name of the Y axis.
 *
 * @example
 * // {
 * //   min: 1,
 * //   processedData: {
 * //    id: 'count',
 * //    data: [{x: '2022-08-01', y: 1}]
 * //   }
 * // }
 *
 * const data = {count.leave.id: 1, cohort: '2022-08-01'}
 * const aggregation = {
 *  function: "count",
 *  path: "leave.id"
 * }
 * const projection = {
 *  function: "count",
 *  path: "leave.id"
 * }
 *
 * const muncher = new Lines({data, aggregation, projection})
 * muncher.call()
 *
 * @constructor
 * @param munched - provide data structure output from the Line muncher class
 * @param data - provide raw data from reports controller
 * @param reportStruct - provide reportStruct in order to read the query projections
 * @param showLegend - provide showLegend in order to show the chart legend
 * @param chartData - provide chartData in order to read the sql projections
 */
const LinesChart: React.FC<Props> = ({ munched, reportStruct, showLegend, chartData }) => {
  const [legends, setLegends] = React.useState<LegendProps[]>([])

  const addLegend = React.useCallback((legend: LegendProps) => {
    setLegends((prevLegends) => {
      const sameId = ({ id }: { id: string }) => id === legend.id
      if (prevLegends.find(sameId) === undefined) {
        return [...prevLegends, legend]
      }
      return prevLegends
    })
  }, [])

  const formatBottomAxis = (value: string) => {
    const projection = reportStruct.props.projections[1]

    if (projection && !validateFieldType(projection, value)) {
      return i18n.t('custom_reports.value_mismatch')
    }

    return projection && formatterForGraph(projection)(value)
  }

  const extraProps = (): any => {
    if (showLegend && (reportStruct.props.projections.length > 2 || chartData?.tertiary_data)) {
      return {
        margin: { top: 30, right: 20, bottom: 60, left: 80 },
        legends: [
          {
            dataFrom: 'keys',
            anchor: 'left',
            direction: 'row',
            justify: false,
            width: 0,
            height: 0,
            translateX: 150,
            itemsSpacing: 2,
            itemHeight: 20,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 20,
            symbolShape: ({ id, size, fill }: { id: string; size: number; fill: string }) => {
              addLegend({ id, size, fill })
              return null
            },
          },
        ],
      }
    }

    return { margin: { top: 30, right: 50, bottom: 60, left: 80 } }
  }

  const addFormat = () => (reportStruct.sqlQuery ? null : { format: formatBottomAxis })

  const chart = (
    <ResponsiveLine
      data={munched}
      xScale={{ type: 'point' }}
      yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        ...addFormat(),
      }}
      animate
      lineWidth={2}
      dotSize={10}
      dotColor="inherit:darker(0.3)"
      dotBorderWidth={2}
      dotBorderColor="#ffffff"
      enableDotLabel
      {...extraProps()}
    />
  )

  return (
    <div className={styles.chartWithLegendsContainer}>
      <div className={styles.chartContainer}>{chart}</div>
      <Legends legends={legends} />
    </div>
  )
}

export default LinesChart
